import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { ApiSurveyDto, ApiSurveyModelService } from '../../../../shared/services/api-surveymodel.service';
import {
  AlertTriggerModel, AlertConfigModel, ApiAlertsConfigurationService, OptionalTagModel, TemplateConfigModel
} from '../../../../shared/services/api-alertsconfiguration.service';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { InfoTooltipComponent } from '../../../../shared/components/ui/info-tooltip/info-tooltip.component';
import { CanDeactivateComponent } from '../survey-builder/survey-builder.guard';

interface CategoryEntry {
  key: string;
  value: TemplateConfigModel;
}

interface TriggerEntry {
  key: string;
  value: AlertTriggerModel;
}

interface OptionalTagEntry {
  key: string;
  value: OptionalTagModel;
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

@Component({
  selector: 'app-survey-alert',
  templateUrl: './survey-alert.component.html',
  styleUrls: ['./survey-alert.component.css'],
  imports: [BreadcrumbComponent, PageLoaderComponent, SurveyHeaderComponent, InfoTooltipComponent]
})
export class SurveyAlertComponent implements OnInit, CanDeactivateComponent {
  survey: ApiSurveyDto | null = null;
  alerts: AlertConfigModel[] = [];
  loading = signal(false);
  error = signal<string | null>(null);

  private organizationId = '';
  private surveyId = '';
  private expandedAlertIds = new Set<string>();
  private expandedSections = new Set<string>();

  // This page is view-only for now — nothing to lose on navigation — but the route already wires
  // up unsavedChangesGuard (canDeactivate), so satisfy its interface trivially rather than have it
  // crash on an undefined showLeaveConfirm signal.
  readonly pendingChangesCount = 0;
  showLeaveConfirm = signal(false);
  setLeaveResolve(): void {}

  constructor(
    private route: ActivatedRoute,
    private organizationStore: OrganizationStoreService,
    private surveyModelService: ApiSurveyModelService,
    private alertsConfigurationService: ApiAlertsConfigurationService) {}

  ngOnInit() {
    // surveySelectedGuard on the route guarantees these are present and valid.
    this.organizationId = this.organizationStore.selectedOrganizationId()!;
    this.surveyId = this.route.snapshot.paramMap.get('id')!;
    this.loadData();
  }

  private loadData(): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      survey: this.surveyModelService.getSurveyModel(this.organizationId, this.surveyId),
      alerts: this.alertsConfigurationService.getConfiguration(this.organizationId, this.surveyId)
    }).subscribe({
      next: ({ survey, alerts }) => {
        this.survey = survey;
        this.alerts = alerts.alerts;
        // A single alert is almost certainly the one the user came here to look at — open it.
        if (this.alerts.length === 1) {
          this.expandedAlertIds.add(this.alerts[0].id);
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load alerts configuration. | ' + err);
        this.loading.set(false);
      }
    });
  }

  toggleAlert(id: string): void {
    if (this.expandedAlertIds.has(id)) this.expandedAlertIds.delete(id);
    else this.expandedAlertIds.add(id);
  }

  isAlertExpanded(id: string): boolean {
    return this.expandedAlertIds.has(id);
  }

  // Generic collapse/expand for smaller nested sections (distribution lists, etc.) — collapsed
  // by default, since there's typically nothing worth glancing at without opening them anyway.
  toggleSection(id: string): void {
    if (this.expandedSections.has(id)) this.expandedSections.delete(id);
    else this.expandedSections.add(id);
  }

  isSectionExpanded(id: string): boolean {
    return this.expandedSections.has(id);
  }

  getCategoryEntries(alert: AlertConfigModel): CategoryEntry[] {
    return Object.entries(alert.categories).map(([key, value]) => ({ key, value }));
  }

  getTriggerEntries(template: TemplateConfigModel): TriggerEntry[] {
    return Object.entries(template.triggers).map(([key, value]) => ({ key, value }));
  }

  getOptionalTagEntries(template: TemplateConfigModel): OptionalTagEntry[] {
    return Object.entries(template.emailTemplate?.optionalTags ?? {}).map(([key, value]) => ({ key, value }));
  }

  hasValue(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
  }

  // Dispatch week-day codes come as e.g. "1-7" or "2,3,6" — 1 = Monday … 7 = Sunday.
  parseWeekDays(code: string): string[] {
    const days = new Set<number>();

    for (const part of code.split(',').map(s => s.trim()).filter(Boolean)) {
      const rangeMatch = part.match(/^(\d)\s*-\s*(\d)$/);
      if (rangeMatch) {
        const start = Number(rangeMatch[1]);
        const end = Number(rangeMatch[2]);
        for (let d = start; d <= end; d++) days.add(d);
      } else if (/^\d$/.test(part)) {
        days.add(Number(part));
      }
    }

    return Array.from(days).sort((a, b) => a - b).map(d => WEEKDAY_NAMES[d - 1] ?? `Day ${d}`);
  }
}
